import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Typography, message, Drawer, List, Avatar, Space } from 'antd'
import Spinner from '../../components/Spinner'
import {
  PhoneOutlined,
  PhoneFilled,
  VideoCameraOutlined,
  VideoCameraFilled,
  TeamOutlined,
  DesktopOutlined,
  StopOutlined,
  InfoCircleOutlined,
  UserOutlined,
  AudioOutlined,
  AudioMutedOutlined
} from '@ant-design/icons'
import { useSelector } from 'react-redux'
import * as liveSessionsService from '../../services/liveSessions'
import useCoursePrivilege from '../../hooks/useCourseprivilege'
import './meeting.css'

const { Title, Text } = Typography

const LiveSessionRoom = () => {
  const { courseId, sessionId } = useParams()
  const user = useSelector((state) => state.auth?.user)
  const { privilege } = useCoursePrivilege(courseId)

  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [left, setLeft] = useState(false)
  const [joined, setJoined] = useState(false)
  const [muted, setMuted] = useState(false)
  const [videoOff, setVideoOff] = useState(false)
  const [screenSharing, setScreenSharing] = useState(false)
  const [participantsOpen, setParticipantsOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [participants, setParticipants] = useState([])
  const [remoteUsers, setRemoteUsers] = useState([])
  const [myUid, setMyUid] = useState(null)
  const [tokenRole, setTokenRole] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))

  const [joinWithMic, setJoinWithMic] = useState(true)
  const [joinWithVideo, setJoinWithVideo] = useState(true)

  const localVideoRef = useRef(null)
  const localScreenRef = useRef(null)
  const clientRef = useRef(null)
  const localTracksRef = useRef([])
  const screenTrackRef = useRef(null)
  const participantsIntervalRef = useRef(null)
  const prejoinPreviewRef = useRef(null)
  const prejoinTrackRef = useRef(null)

  const isHost = tokenRole != null ? tokenRole === 'host' : (privilege === 'instructor' || privilege === 'admin')
  const validSessionId = sessionId && sessionId !== 'undefined' && String(sessionId).length === 24

  // Clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!validSessionId) {
      setLoading(false)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const data = await liveSessionsService.getOne(sessionId)
        if (cancelled) return
        setSession(data)
      } catch (e) {
        if (cancelled) return
        message.error(e?.response?.data?.message || 'Failed to load session')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [sessionId, validSessionId])

  // Pre-join preview
  useEffect(() => {
    if (!joinWithVideo || !session || joined) {
      if (prejoinTrackRef.current) {
        prejoinTrackRef.current.close()
        prejoinTrackRef.current = null
      }
      return
    }
    let cancelled = false
    import('agora-rtc-sdk-ng').then(({ default: AgoraRTC }) => {
      AgoraRTC.createCameraVideoTrack({ encoderConfig: '720p_2' })
        .then((t) => {
          if (cancelled) {
            t.close()
            return
          }
          prejoinTrackRef.current = t
          if (prejoinPreviewRef.current) t.play(prejoinPreviewRef.current)
        })
        .catch(() => { })
    })
    return () => {
      cancelled = true
      if (prejoinTrackRef.current) {
        prejoinTrackRef.current.close()
        prejoinTrackRef.current = null
      }
    }
  }, [joinWithVideo, session, joined])

  const fetchParticipants = async () => {
    if (!sessionId) return
    try {
      const list = await liveSessionsService.getParticipants(sessionId)
      setParticipants(list)
    } catch (_) { }
  }

  const joinChannel = async () => {
    if (!session) return
    setJoining(true)
    try {
      const { token, channelName, uid, appId, role: serverRole } = await liveSessionsService.getAgoraToken(sessionId)
      setMyUid(uid)
      setTokenRole(serverRole)

      const AgoraRTC = (await import('agora-rtc-sdk-ng')).default
      const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })
      clientRef.current = client

      await client.join(appId, channelName, token, uid)

      const displayName = user?.name || user?.username || `User ${uid}`
      try {
        await liveSessionsService.registerJoin(sessionId, uid, displayName)
      } catch (_) { }

      const tracks = []
      if (joinWithMic) {
        const audio = await AgoraRTC.createMicrophoneAudioTrack()
        tracks.push(audio)
      }
      if (joinWithVideo) {
        const video = await AgoraRTC.createCameraVideoTrack()
        tracks.push(video)
        if (localVideoRef.current) video.play(localVideoRef.current)
      }

      localTracksRef.current = tracks
      if (tracks.length > 0) await client.publish(tracks)

      setMuted(!joinWithMic)
      setVideoOff(!joinWithVideo)

      client.on('user-published', async (userRemote, mediaType) => {
        await client.subscribe(userRemote, mediaType)
        if (mediaType === 'video') {
          setRemoteUsers(prev => {
            if (prev.find(u => u.uid === userRemote.uid)) return prev
            return [...prev, userRemote]
          })
        }
        if (mediaType === 'audio') {
          userRemote.audioTrack?.play()
        }
      })

      client.on('user-unpublished', (userRemote, mediaType) => {
        if (mediaType === 'video') {
          setRemoteUsers(prev => prev.filter(u => u.uid !== userRemote.uid))
        }
      })

      client.on('user-left', (userRemote) => {
        setRemoteUsers(prev => prev.filter(u => u.uid !== userRemote.uid))
      })

      setJoined(true)
      participantsIntervalRef.current = setInterval(fetchParticipants, 5000)
      fetchParticipants()
    } catch (e) {
      console.error(e)
      message.error('Failed to join the meeting')
    } finally {
      setJoining(false)
    }
  }

  const leaveChannel = async () => {
    if (clientRef.current) {
      localTracksRef.current.forEach(t => {
        t.stop()
        t.close()
      })
      await clientRef.current.leave()
      clientRef.current = null
    }
    if (participantsIntervalRef.current) clearInterval(participantsIntervalRef.current)
    setJoined(false)
    setLeft(true)
  }

  const toggleMute = () => {
    const audio = localTracksRef.current.find(t => t.trackMediaType === 'audio')
    if (audio) {
      audio.setEnabled(muted)
      setMuted(!muted)
    }
  }

  const toggleVideo = () => {
    const video = localTracksRef.current.find(t => t.trackMediaType === 'video')
    if (video) {
      video.setEnabled(videoOff)
      setVideoOff(!videoOff)
    }
  }

  const toggleScreenShare = async () => {
    if (screenSharing) {
      await clientRef.current.unpublish(screenTrackRef.current)
      screenTrackRef.current.stop()
      screenTrackRef.current.close()
      screenTrackRef.current = null
      setScreenSharing(false)
      return
    }
    try {
      const AgoraRTC = (await import('agora-rtc-sdk-ng')).default
      const screenTrack = await AgoraRTC.createScreenVideoTrack()
      screenTrackRef.current = screenTrack
      await clientRef.current.publish(screenTrack)
      setScreenSharing(true)
      screenTrack.on('track-ended', () => {
        setScreenSharing(false)
      })
    } catch (e) {
      message.error('Screen sharing failed')
    }
  }

  if (loading) return <div className="meeting-loading"><Spinner size="large" /></div>
  if (left) return <div className="meeting-loading"><Title level={3} style={{ color: '#fff' }}>You left the meeting</Title><Button onClick={() => window.close()}>Return to Dashboard</Button></div>

  const totalParticipantsCount = remoteUsers.length + (joined ? 1 : 0)

  return (
    <div className="meeting-root">
      {!joined ? (
        <div className="meeting-prejoin">
          <div className="meeting-prejoin-card">
            <div className="meeting-prejoin-preview">
              <div ref={prejoinPreviewRef} className="meeting-prejoin-preview-inner" />
              {!joinWithVideo && <div className="meeting-prejoin-preview-placeholder"><UserOutlined style={{ fontSize: 64 }} /> <Text style={{ color: '#fff' }}>Camera is off</Text></div>}
              <div className="meeting-prejoin-controls">
                <Button
                  className={`meet-btn ${!joinWithMic ? 'off' : ''}`}
                  icon={joinWithMic ? <AudioOutlined /> : <AudioMutedOutlined />}
                  onClick={() => setJoinWithMic(!joinWithMic)}
                />
                <Button
                  className={`meet-btn ${!joinWithVideo ? 'off' : ''}`}
                  icon={joinWithVideo ? <VideoCameraOutlined /> : <VideoCameraFilled />}
                  onClick={() => setJoinWithVideo(!joinWithVideo)}
                />
              </div>
            </div>
          </div>
          <div className="meeting-prejoin-info">
            <h1>Ready to join?</h1>
            <p>{session?.title}</p>
            <Button
              type="primary"
              size="large"
              className="meeting-join-now-btn"
              onClick={joinChannel}
              loading={joining}
            >
              Join now
            </Button>
          </div>
        </div>
      ) : (
        <main className="meeting-main">
          <div className="meeting-videos" data-count={totalParticipantsCount}>
            {/* Local Video */}
            <div className="meeting-tile">
              <div ref={localVideoRef} className="meeting-video-inner" />
              {videoOff && <div className="meeting-video-placeholder"><UserOutlined style={{ fontSize: 48 }} /></div>}
              <span className="meeting-tile-label">You</span>
            </div>

            {/* Remote Videos */}
            {remoteUsers.map(userRemote => (
              <div key={userRemote.uid} className="meeting-tile remote-user-node" id={`remote-${userRemote.uid}`}>
                <div
                  className="meeting-video-inner"
                  ref={el => el && userRemote.videoTrack?.play(el)}
                />
                <span className="meeting-tile-label">Remote User</span>
              </div>
            ))}

            {/* Screen Share */}
            {screenSharing && (
              <div className="meeting-tile">
                <div ref={localScreenRef} className="meeting-video-inner" />
                <span className="meeting-tile-label">Your Presentation</span>
              </div>
            )}
          </div>

          <footer className="meeting-footer">
            <div className="meeting-footer-left">
              <span>{currentTime}</span>
              <div className="meeting-time-divider" />
              <span style={{ color: 'var(--meet-text-secondary)' }}>{session?.title}</span>
            </div>

            <div className="meeting-footer-center">
              <Button
                className={`meet-btn ${muted ? 'off' : ''}`}
                icon={muted ? <AudioMutedOutlined /> : <AudioOutlined />}
                onClick={toggleMute}
              />
              <Button
                className={`meet-btn ${videoOff ? 'off' : ''}`}
                icon={videoOff ? <StopOutlined /> : <VideoCameraOutlined />}
                onClick={toggleVideo}
              />
              <Button
                className={`meet-btn ${screenSharing ? 'active' : ''}`}
                icon={<DesktopOutlined />}
                onClick={toggleScreenShare}
              />
              <Button
                className="meet-btn danger"
                icon={<PhoneOutlined rotate={225} />}
                onClick={leaveChannel}
              />
            </div>

            <div className="meeting-footer-right">
              <Button type="text" className="meet-btn" icon={<InfoCircleOutlined />} onClick={() => setInfoOpen(true)} />
              <Button type="text" className="meet-btn" icon={<TeamOutlined />} onClick={() => setParticipantsOpen(true)} />
            </div>
          </footer>
        </main>
      )}

      <Drawer
        title="Participants"
        onClose={() => setParticipantsOpen(false)}
        open={participantsOpen}
        width={350}
        className="meeting-drawer"
      >
        <List
          itemLayout="horizontal"
          dataSource={participants}
          renderItem={(p) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={<span style={{ color: '#fff' }}>{p.name} {p.uid === myUid ? '(You)' : ''}</span>}
                description={<span style={{ color: 'var(--meet-text-secondary)' }}>In meeting</span>}
              />
            </List.Item>
          )}
        />
      </Drawer>

      <Drawer
        title="Meeting Details"
        onClose={() => setInfoOpen(false)}
        open={infoOpen}
        width={300}
        className="meeting-drawer"
      >
        <div style={{ padding: 16 }}>
          <Title level={5} style={{ color: '#fff' }}>{session?.title}</Title>
          <Text style={{ color: 'var(--meet-text-secondary)' }}>{session?.description || 'Ongoing live session'}</Text>
        </div>
      </Drawer>
    </div>
  )
}

export default LiveSessionRoom
